// client/src/coms/views/canvas_parts/CanvasBrushPresetPanel.js
import React, { useEffect, useMemo, useState } from 'react';
import ImportedBrushImportModal from './ImportedBrushImportModal';

const DEFAULT_TAB_ID = 'default';
const ALL_TAB_ID = 'all';

function cleanTabLabel(value) {
  const text = String(value || '').trim();
  return text || 'Imported';
}

const FAVOURITES_TAB_ID = 'favourites';

// Imported brushes keep their source set/folder name as the tab.
function getBrushCategory(brush) {
  return cleanTabLabel((brush && (brush.folder || brush.category || brush.brushCategory || brush.type || brush.group)) || 'Imported');
}

function DefaultRoundBrushThumb({ eraser = false }) {
  return (
    <div
      style={{
        width: '54%',
        height: '54%',
        borderRadius: '50%',
        border: eraser ? '3px solid rgba(25,25,25,0.84)' : 'none',
        background: eraser ? 'rgba(255,255,255,0.85)' : '#111',
        boxShadow: eraser ? 'inset 0 0 0 2px rgba(255,255,255,0.62)' : '0 0 0 1px rgba(0,0,0,0.28)',
      }}
    />
  );
}

function PresetTile({ active, title, children, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        minWidth: 0,
        aspectRatio: '1 / 1',
        borderRadius: 4,
        border: active ? '2px solid rgba(122,181,255,0.92)' : '1px solid rgba(255,255,255,0.12)',
        background: '#f8f8f8',
        color: '#151515',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: 'pointer',
        padding: 4,
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {children}
    </button>
  );
}

function ImportedBrushThumb({ brush }) {
  const dataUrl = brush && (brush.thumbnailDataUrl || brush.originalTipDataUrl);
  if (dataUrl) {
    return <img src={dataUrl} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none', userSelect: 'none' }} />;
  }

  return (
    <div
      style={{
        width: '72%',
        height: '32%',
        borderRadius: '50%',
        background: '#111',
        transform: 'rotate(-24deg)',
        opacity: 0.86,
      }}
    />
  );
}

function BrushTabButton({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        height: 24,
        borderRadius: 5,
        border: active ? '1px solid rgba(122,181,255,0.72)' : '1px solid rgba(255,255,255,0.12)',
        background: active ? 'rgba(122,181,255,0.18)' : 'rgba(255,255,255,0.055)',
        color: active ? 'rgba(245,249,255,0.98)' : 'rgba(245,247,250,0.72)',
        padding: '0 8px',
        fontSize: 10,
        fontWeight: 900,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
      }}
    >
      <span>{label}</span>
      {Number.isFinite(count) ? <span style={{ opacity: 0.62 }}>{count}</span> : null}
    </button>
  );
}

export default function CanvasBrushPresetPanel({
  activeTool,
  activeBrushPreset,
  setActiveTool,
  setActiveBrushPreset,
  importedBrushPresets = [],
  activeImportedBrushId = '',
  setActiveImportedBrushId,
  onAddImportedBrushPresets,
  paintMode = false,
}) {
  const [importOpen, setImportOpen] = useState(false);
  const brushes = Array.isArray(importedBrushPresets) ? importedBrushPresets.filter(Boolean) : [];
  const isEraserTool = activeTool === 'eraser';
  const isPaintBrushTool = activeTool === 'paint' && paintMode;

  const categoryTabs = useMemo(() => {
    const counts = new Map();
    brushes.forEach((brush) => {
      const category = getBrushCategory(brush);
      counts.set(category, (counts.get(category) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([id, count]) => ({ id, label: id, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [brushes]);

  const [activeTab, setActiveTab] = useState(ALL_TAB_ID);

  const favouriteCount = brushes.filter((brush) => brush.favourite).length;

  useEffect(() => {
    if (activeTab === ALL_TAB_ID || activeTab === DEFAULT_TAB_ID) return;
    if (activeTab === FAVOURITES_TAB_ID && favouriteCount) return;
    if (!categoryTabs.some((tab) => tab.id === activeTab)) setActiveTab(ALL_TAB_ID);
  }, [activeTab, categoryTabs, favouriteCount]);

  const visibleBrushes = activeTab === ALL_TAB_ID || activeTab === DEFAULT_TAB_ID
    ? brushes
    : activeTab === FAVOURITES_TAB_ID
      ? brushes.filter((brush) => brush.favourite)
      : brushes.filter((brush) => getBrushCategory(brush) === activeTab);

  const showDefaultBrush = activeTab === ALL_TAB_ID || activeTab === DEFAULT_TAB_ID;

  function selectDefaultBrush() {
    if (!isEraserTool && !isPaintBrushTool) {
      if (typeof setActiveTool === 'function') setActiveTool('pen');
      if (typeof setActiveBrushPreset === 'function') setActiveBrushPreset('pen');
    }
    if (typeof setActiveImportedBrushId === 'function') setActiveImportedBrushId('');
  }

  function selectImported(brushId) {
    if (!isEraserTool && !isPaintBrushTool) {
      if (typeof setActiveTool === 'function') setActiveTool('pen');
      if (typeof setActiveBrushPreset === 'function') setActiveBrushPreset('pen');
    }
    if (typeof setActiveImportedBrushId === 'function') setActiveImportedBrushId(brushId);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'thin',
          paddingBottom: 2,
        }}
      >
        <BrushTabButton
          label="All"
          count={brushes.length + 1}
          active={activeTab === ALL_TAB_ID}
          onClick={() => setActiveTab(ALL_TAB_ID)}
        />
        <BrushTabButton
          label="Default"
          count={1}
          active={activeTab === DEFAULT_TAB_ID}
          onClick={() => setActiveTab(DEFAULT_TAB_ID)}
        />
        {favouriteCount ? (
          <BrushTabButton
            label="★ Favourites"
            count={favouriteCount}
            active={activeTab === FAVOURITES_TAB_ID}
            onClick={() => setActiveTab(FAVOURITES_TAB_ID)}
          />
        ) : null}
        {categoryTabs.map((tab) => (
          <BrushTabButton
            key={tab.id}
            label={tab.label}
            count={tab.count}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          />
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 4,
        }}
      >
        {showDefaultBrush ? (
          <PresetTile
            title={isEraserTool ? 'Default round eraser' : isPaintBrushTool ? 'Default oil-paint brush' : 'Default round brush'}
            active={((activeTool === 'pen' && activeBrushPreset === 'pen') || activeTool === 'eraser' || isPaintBrushTool) && !activeImportedBrushId}
            onClick={selectDefaultBrush}
          >
            <DefaultRoundBrushThumb eraser={isEraserTool} />
          </PresetTile>
        ) : null}

        {visibleBrushes.map((brush) => (
          <PresetTile
            key={brush.id}
            title={brush.paintable === false ? `${brush.name || 'Imported brush'} (kept, not paintable yet)` : brush.name || 'Imported brush'}
            active={((activeTool === 'pen' && activeBrushPreset === 'pen') || activeTool === 'eraser' || isPaintBrushTool) && activeImportedBrushId === brush.id}
            onClick={() => (brush.paintable === false ? setImportOpen(true) : selectImported(brush.id))}
          >
            <div style={{ width: '100%', height: '100%', opacity: brush.paintable === false ? 0.35 : 1 }}>
              <ImportedBrushThumb brush={brush} />
            </div>
          </PresetTile>
        ))}
      </div>

      {!visibleBrushes.length && !showDefaultBrush ? (
        <div style={{ color: 'rgba(255,255,255,0.48)', fontSize: 11, fontWeight: 750, padding: '5px 1px' }}>
          No brushes in this tab.
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {typeof onAddImportedBrushPresets === 'function' ? (
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          style={{
            height: 28,
            borderRadius: 5,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(255,255,255,0.07)',
            color: 'rgba(245,247,250,0.94)',
            padding: '0 9px',
            fontSize: 11,
            fontWeight: 850,
            cursor: 'pointer',
          }}
        >
          Import / manage
        </button>
        ) : null}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.48)', fontSize: 10, fontWeight: 750 }}>
          {brushes.length ? `${brushes.length} imported brush${brushes.length === 1 ? '' : 'es'}` : 'Only default round brush loaded'}
        </span>
      </div>

      <ImportedBrushImportModal
        open={importOpen}
        importedBrushPresets={importedBrushPresets}
        activeImportedBrushId={activeImportedBrushId}
        onClose={() => setImportOpen(false)}
        onAddImportedBrushPresets={onAddImportedBrushPresets}
        onSetActiveImportedBrushId={setActiveImportedBrushId}
      />
    </div>
  );
}
